<template>
  <div class="notification-management">
    <div class="page-header">
      <h2>通知管理</h2>
      <el-button type="primary" @click="showAddDialog">发送新通知</el-button>
    </div>

    <!-- 通知列表 -->
    <el-table :data="notifications" style="width: 100%" v-loading="loading">
      <el-table-column prop="id" label="ID" width="80" />
      <el-table-column prop="title" label="标题" />
      <el-table-column prop="content" label="内容" show-overflow-tooltip />
      <el-table-column prop="target_type" label="发送对象" width="100">
        <template #default="{ row }">
          <el-tag :type="row.target_type === 'all' ? 'success' : 'warning'">
            {{ row.target_type === 'all' ? '全部用户' : '指定用户' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="created_at" label="发送时间" width="180">
        <template #default="{ row }">
          {{ formatTime(row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="150" fixed="right">
        <template #default="{ row }">
          <el-button-group>
            <el-button 
              size="small" 
              type="primary" 
              @click="handleView(row)"
            >查看</el-button>
            <el-button 
              size="small" 
              type="danger" 
              @click="handleDelete(row)"
            >删除</el-button>
          </el-button-group>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 发送通知对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      width="600px"
    >
      <el-form 
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="100px"
      >
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入通知标题" />
        </el-form-item>
        <el-form-item label="内容" prop="content">
          <el-input
            v-model="form.content"
            type="textarea"
            :rows="4"
            placeholder="请输入通知内容"
          />
        </el-form-item>
        <el-form-item label="发送对象" prop="target_type">
          <el-radio-group v-model="form.target_type">
            <el-radio label="all">全部用户</el-radio>
            <el-radio label="specific">指定用户</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item 
          v-if="form.target_type === 'specific'"
          label="选择用户" 
          prop="user_ids"
        >
          <el-select
            v-model="form.user_ids"
            multiple
            filterable
            placeholder="请选择用户"
          >
            <el-option
              v-for="user in users"
              :key="user.id"
              :label="user.username"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting">
            发送
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 查看通知详情 -->
    <el-dialog
      v-model="detailVisible"
      title="通知详情"
      width="500px"
    >
      <div class="notification-detail" v-if="currentNotification">
        <h3>{{ currentNotification.title }}</h3>
        <p class="notification-time">
          发送时间：{{ formatTime(currentNotification.created_at) }}
        </p>
        <div class="notification-content">
          {{ currentNotification.content }}
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { FormInstance, FormRules } from 'element-plus'
import requestV2 from '@/utils/requestV2';


interface Notification {
  id: number
  title: string
  content: string
  target_type: 'all' | 'specific'
  user_id?: number
  created_at: string
}

interface User {
  id: number
  username: string
}

const notifications = ref<Notification[]>([])
const loading = ref(false)
const currentPage = ref(1)
const pageSize = ref(10)
const total = ref(0)
const dialogVisible = ref(false)
const detailVisible = ref(false)
const submitting = ref(false)
const users = ref<User[]>([])
const currentNotification = ref<Notification | null>(null)

const form = ref({
  title: '',
  content: '',
  target_type: 'all',
  user_ids: [] as number[]
})

const rules: FormRules = {
  title: [
    { required: true, message: '请输入通知标题', trigger: 'blur' },
    { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
  ],
  content: [
    { required: true, message: '请输入通知内容', trigger: 'blur' }
  ],
  target_type: [
    { required: true, message: '请选择发送对象', trigger: 'change' }
  ],
  user_ids: [
    { 
      required: true, 
      message: '请选择用户', 
      trigger: 'change',
      validator: (rule, value, callback) => {
        if (form.value.target_type === 'specific' && (!value || value.length === 0)) {
          callback(new Error('请选择至少一个用户'))
        } else {
          callback()
        }
      }
    }
  ]
}

const formRef = ref<FormInstance>()

// 获取通知列表
const fetchNotifications = async () => {
  try {
    const params = new URLSearchParams({
      page: currentPage.value.toString(),
      limit: pageSize.value.toString()
    });

    console.log('请求参数:', Object.fromEntries(params));

    const response = await requestV2.get(`/notifications?${params}`);

    if (!(response.data.code == 200)) {
      const errorData = await response.data.data();
      console.error('服务器错误:', errorData);
      throw new Error(errorData.message || '获取通知列表失败');
    }

    const data = await await response.data.data;
    console.log('获取到的数据:', data);
    
    total.value = response.data.total;
  } catch (error) {
    console.error('获取通知列表失败:', error);
    // ElMessage.error(error.message || '获取通知列表失败');
  }
};

// 获取用户列表
const fetchUsers = async () => {
  try {
    const response = await requestV2.get('/system/users/list',{})
    
    if (!(response.data.code == 200)) {
      throw new Error('获取用户列表失败')
    }
    
    const data = await response.data.data
    users.value = data
  } catch (error) {
    console.error('获取用户列表失败:', error)
    ElMessage.error('获取用户列表失败')
  }
}

// 发送通知
const handleSubmit = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      submitting.value = true
      try {
        const response = await requestV2.post('/notifications',JSON.stringify(form.value))
        
        // if (!response.ok) {
        //   throw new Error('发送通知失败')
        // }
        
        ElMessage.success('通知发送成功')
        dialogVisible.value = false
        fetchNotifications()
      } catch (error) {
        console.error('发送通知失败:', error)
        ElMessage.error('发送通知失败')
      } finally {
        submitting.value = false
      }
    }
  })
}

// 删除通知
const handleDelete = async (notification: Notification) => {
  try {
    await ElMessageBox.confirm('确定要删除这条通知吗？', '提示', {
      type: 'warning'
    })
    
    const response = await fetch(`/api/admin/notifications/${notification.id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    })
    
    if (!response.ok) {
      throw new Error('删除通知失败')
    }
    
    ElMessage.success('删除成功')
    fetchNotifications()
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除通知失败:', error)
      ElMessage.error('删除通知失败')
    }
  }
}

// 查看通知详情
const handleView = (notification: Notification) => {
  currentNotification.value = notification
  detailVisible.value = true
}

// 显示添加对话框
const showAddDialog = () => {
  form.value = {
    title: '',
    content: '',
    target_type: 'all',
    user_ids: []
  }
  dialogVisible.value = true
}

// 分页处理
const handleSizeChange = (val: number) => {
  pageSize.value = val
  fetchNotifications()
}

const handleCurrentChange = (val: number) => {
  currentPage.value = val
  fetchNotifications()
}

// 格式化时间
const formatTime = (time: string) => {
  return new Date(time).toLocaleString()
}

onMounted(() => {
  fetchNotifications()
  fetchUsers()
})
</script>

<style scoped>
.notification-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.notification-detail {
  padding: 20px;
}

.notification-detail h3 {
  margin: 0 0 15px;
  color: #303133;
}

.notification-time {
  color: #909399;
  font-size: 14px;
  margin-bottom: 20px;
}

.notification-content {
  white-space: pre-wrap;
  line-height: 1.6;
}
</style> 